import type {ActionEnvelope, ChatState} from '@microsoft/agent-host-protocol'
import type {RelayTurnDispatch} from '@github-ui/ahp-relay/relay-connection-runtime'
import {sessionChannel} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {
  DispatchReceipt,
  EstablishedAhpSessionIdentity,
  PreparedPrompt,
  SubmissionAttempt,
  SubmissionRecovery,
} from './contracts'
import {UserFacingSubmissionError} from './submission-error'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export function isSubmissionRecoveryEnabled(): boolean {
  return isFeatureEnabled('copilot_chat_ahp_submission_recover')
}

type SubmissionFailurePhase = 'unsent' | 'unknown' | 'rejected' | 'accepted'

export function submissionRecoveryMessage(phase: SubmissionFailurePhase): string {
  return phase === 'accepted'
    ? 'The host accepted your message, but the client could not finish submission handling. Check the conversation for its result.'
    : phase === 'unknown'
      ? 'We could not confirm whether the host accepted your message. Reconnect and check its status before sending another message. Your draft is still available.'
      : phase === 'rejected'
        ? 'The host rejected your message. Your draft is still available. Reconnect before trying again.'
        : 'Your message did not reach prompt dispatch because session preparation failed. Your draft is still available. Reconnect before trying again.'
}

export class SubmissionRecoveryError extends UserFacingSubmissionError {
  readonly recovery: SubmissionRecovery
  readonly phase: SubmissionFailurePhase
  constructor(recovery: SubmissionRecovery, phase: SubmissionFailurePhase, cause?: unknown) {
    super(submissionRecoveryMessage(phase))
    this.name = 'SubmissionRecoveryError'
    this.recovery = recovery
    this.phase = phase
    this.cause = cause
  }

  static forAttempt(recovery: SubmissionRecovery, attempt: SubmissionAttempt | undefined, cause: unknown) {
    return new SubmissionRecoveryError(
      recovery,
      attempt?.phase === 'accepted' || attempt?.phase === 'rejected'
        ? attempt.phase
        : attempt?.dispatch
          ? 'unknown'
          : 'unsent',
      cause,
    )
  }
}

export function submissionRecoveryFromError(error: unknown): SubmissionRecovery | undefined {
  const seen = new Set<Error>()
  while (error instanceof Error && !seen.has(error)) {
    if (error instanceof SubmissionRecoveryError) return error.recovery
    seen.add(error)
    error = error.cause
  }
  return undefined
}

export function submissionIsUnsettled(attempt: SubmissionAttempt | undefined): boolean {
  return attempt?.phase === 'dispatched' || attempt?.phase === 'unknown'
}

// Compare dispatch snapshots exactly, including absent client ID and sequence fields.
export function isSameDispatchedAttempt(
  before: SubmissionAttempt | undefined,
  current: SubmissionAttempt | undefined,
): current is SubmissionAttempt & {readonly dispatch: RelayTurnDispatch} {
  return Boolean(
    before?.dispatch &&
    current?.dispatch &&
    current.requestId === before.requestId &&
    current.dispatch.turnId === before.dispatch.turnId &&
    current.dispatch.channel === before.dispatch.channel &&
    current.dispatch.clientId === before.dispatch.clientId &&
    current.dispatch.clientSeq === before.dispatch.clientSeq,
  )
}

export class SubmissionAttemptStore {
  #attempt: SubmissionAttempt | undefined
  readonly #listeners = new Set<() => void>()

  getSnapshot = (): SubmissionAttempt | undefined => this.#attempt
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener)
    return () => this.#listeners.delete(listener)
  }

  #set(attempt: SubmissionAttempt): void {
    this.#attempt = attempt
    for (const listener of this.#listeners) listener()
  }

  begin(requestId: string, prompt: PreparedPrompt): void {
    this.#set({requestId, prompt: structuredClone(prompt), phase: 'preparing'})
  }

  dispatched(requestId: string, dispatch: RelayTurnDispatch): void {
    if (this.#attempt?.requestId !== requestId) return
    this.#set({...this.#attempt, dispatch, phase: this.#attempt.dispatch ? this.#attempt.phase : 'dispatched'})
  }

  receipt(receipt: DispatchReceipt): void {
    if (this.#attempt?.requestId !== receipt.requestId) return
    this.#set({...this.#attempt, receipt})
  }

  failed(requestId: string): void {
    if (this.#attempt?.requestId !== requestId) return
    if (this.#attempt.phase === 'accepted' || this.#attempt.phase === 'rejected') return
    this.#set({...this.#attempt, phase: this.#attempt.dispatch ? 'unknown' : 'unsent'})
  }

  action(envelope: ActionEnvelope): void {
    const attempt = this.#attempt
    const dispatch = attempt?.dispatch
    if (
      !attempt ||
      attempt.phase === 'accepted' ||
      attempt.phase === 'rejected' ||
      !dispatch ||
      envelope.channel !== dispatch.channel ||
      envelope.action.type !== 'chat/turnStarted' ||
      envelope.action.turnId !== dispatch.turnId ||
      (dispatch.clientId !== undefined && envelope.origin?.clientId !== dispatch.clientId) ||
      (dispatch.clientSeq !== undefined && envelope.origin?.clientSeq !== dispatch.clientSeq)
    ) {
      return
    }
    this.#set({...attempt, phase: envelope.rejectionReason ? 'rejected' : 'accepted'})
  }

  reconcile(state: ChatState | undefined): void {
    const attempt = this.#attempt
    if (
      attempt?.phase === 'accepted' ||
      attempt?.phase === 'rejected' ||
      !attempt?.dispatch ||
      state?.resource !== attempt.dispatch.channel ||
      (state.activeTurn?.id !== attempt.dispatch.turnId &&
        !state.turns.some(turn => turn.id === attempt.dispatch?.turnId))
    ) {
      return
    }
    this.#set({...attempt, phase: 'accepted'})
  }
}

// Page-local only: keep uncertain attempts across runtime replacement, not across reloads.
const stores = new Map<string, SubmissionAttemptStore>()

export function submissionIdentityKey(identity: EstablishedAhpSessionIdentity): string {
  return JSON.stringify([
    identity.taskId,
    identity.environmentId,
    identity.sessionId,
    identity.channel ?? sessionChannel(identity.sessionId),
  ])
}

export function submissionStoreFor(identity: EstablishedAhpSessionIdentity): SubmissionAttemptStore {
  const key = submissionIdentityKey(identity)
  let store = stores.get(key)
  if (!store) {
    store = new SubmissionAttemptStore()
    stores.set(key, store)
  }
  return store
}
